import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

export interface WordmarkProps {
  size?: number;
  dark?: boolean;
  ariaHidden?: boolean;
}

/**
 * Wordmark — PREDIKT. wordmark (mono, with orange period).
 *
 * NOTE: The design's Wordmark is HTML/CSS, NOT an SVG (site-brand.jsx lines 24-36).
 * Port uses <span> with inline styles matching the original (BRP-01, D-14..D-17).
 *
 * Changes from original:
 *   1. TypeScript prop types.
 *   2. Hex literals replaced with CSS var tokens (BRP-02).
 *   3. Accessible sr-only <title> from i18n (D-15).
 *
 * Default size=32: matches design default.
 */
export function Wordmark({
  size = 32,
  dark = false,
  ariaHidden = false,
}: WordmarkProps): JSX.Element {
  const { t } = useTranslation();
  const titleId = useId();
  const title = t('brand.wordmark.title');

  const a11yProps = ariaHidden
    ? ({ 'aria-hidden': true } as const)
    : ({ role: 'img', 'aria-labelledby': titleId } as const);

  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: size,
        fontWeight: 700,
        letterSpacing: size * 0.04,
        color: dark ? 'var(--color-paper)' : 'var(--color-ink)',
        lineHeight: 1,
        display: 'inline-flex',
      }}
      {...a11yProps}
    >
      {!ariaHidden && (
        <span id={titleId} className="sr-only">
          {title}
        </span>
      )}
      <span aria-hidden="true">PREDIKT</span>
      <span aria-hidden="true" style={{ color: 'var(--color-orange)', marginLeft: -size * 0.06 }}>
        .
      </span>
    </span>
  );
}

export default Wordmark;
